"use client";

import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

/**
 * CAvatar - Shared avatar component
 *
 * Displays initials, icons, or images in a styled container.
 * Supports multiple sizes, colors, and shapes.
 *
 * Props:
 *   - initials: string (2 characters max, displayed as text)
 *   - icon: ReactNode (icon element to display)
 *   - src: string (image URL - future support)
 *   - size: "xs" | "sm" | "md" | "lg" | "xl" (default: "md")
 *   - color: accent color variant (default: "primary")
 *   - shape: "circle" | "rounded" (default: "rounded")
 *   - className: additional CSS classes
 */

export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";
export type AvatarColor =
  | "primary"
  | "emerald"
  | "amber"
  | "red"
  | "blue"
  | "violet"
  | "teal"
  | "indigo"
  | "slate";
export type AvatarShape = "circle" | "rounded";

export interface CAvatarProps {
  initials?: string;
  icon?: ReactNode;
  size?: AvatarSize;
  color?: AvatarColor;
  shape?: AvatarShape;
  className?: string;
}

// Size configuration
const sizeConfig: Record<
  AvatarSize,
  { container: string; text: string; icon: string }
> = {
  xs: { container: "w-6 h-6", text: "text-[10px]", icon: "w-3 h-3" },
  sm: { container: "w-8 h-8", text: "text-xs", icon: "w-4 h-4" },
  md: { container: "w-10 h-10", text: "text-sm", icon: "w-5 h-5" },
  lg: { container: "w-12 h-12", text: "text-base", icon: "w-6 h-6" },
  xl: { container: "w-14 h-14", text: "text-lg", icon: "w-7 h-7" },
};

// Color configuration
const colorConfig: Record<AvatarColor, string> = {
  primary: "bg-primary/10 text-primary dark:bg-primary/20",
  emerald:
    "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400",
  amber: "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
  red: "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400",
  blue: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
  violet:
    "bg-violet-100 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400",
  teal: "bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400",
  indigo:
    "bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400",
  slate: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
};

// Shape configuration
const shapeConfig: Record<AvatarShape, string> = {
  circle: "rounded-full",
  rounded: "rounded-lg",
};

const CAvatar = ({
  initials,
  icon,
  size = "md",
  color = "primary",
  shape = "rounded",
  className,
}: CAvatarProps) => {
  const sizeStyles = sizeConfig[size];
  const colorStyles = colorConfig[color];
  const shapeStyles = shapeConfig[shape];

  // Get display content
  const displayInitials = initials?.slice(0, 2).toUpperCase();

  return (
    <div
      className={cn(
        "flex items-center justify-center font-semibold flex-shrink-0",
        sizeStyles.container,
        colorStyles,
        shapeStyles,
        className,
      )}
    >
      {icon ? (
        <span className={sizeStyles.icon}>{icon}</span>
      ) : (
        <span className={sizeStyles.text}>{displayInitials}</span>
      )}
    </div>
  );
};

export default CAvatar;
export { CAvatar };
